// L0 — Elegir Tenant (F0-4).
//
// Aparece solo cuando un usuario autenticado tiene >1 tenant disponible y aún
// no eligió ninguno. Usa AuthLayout (centrado, branding consistente).

import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { Role } from 'schemas';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { Spinner } from '../../components/ui/spinner';
import { useAuth } from '../../lib/auth/useAuth';
import { identidadUsuario } from '../../lib/identidad';

type LocationState = { from?: { pathname?: string } } | null;

export function ChooseTenantPage() {
  const { state, switchTenant, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (state.kind !== 'signed_in') {
    return <Navigate to="/login" replace />;
  }

  const tenants = state.claims.tenants_disponibles;

  if (tenants.length <= 1) {
    return <Navigate to="/" replace />;
  }

  async function handleChoose(tenantId: string) {
    setSwitching(tenantId);
    setError(null);
    const { error: switchError } = await switchTenant(tenantId);
    setSwitching(null);
    if (switchError) {
      setError(switchError.message);
      return;
    }
    const from = (location.state as LocationState)?.from?.pathname ?? '/';
    navigate(from, { replace: true });
  }

  return (
    <AuthLayout>
      <p className="mb-6 text-center text-sm text-fg-secondary">
        Hola <strong className="text-fg">{identidadUsuario(state.claims.email).etiqueta}</strong>
      </p>

      <div className="mb-2 px-1">
        <h2 className="text-base font-medium text-fg">Elija su organización</h2>
        <p className="mt-1 text-xs text-fg-secondary">
          Su cuenta tiene acceso a {tenants.length} tenants. Puede cambiar desde el menú.
        </p>
      </div>

      <ul className="flex flex-col gap-2">
        {tenants.map((t) => {
          const isSwitching = switching === t.id;
          const disabled = switching !== null;
          return (
            <li key={t.id}>
              <button
                disabled={disabled}
                onClick={() => handleChoose(t.id)}
                className="group flex w-full items-center justify-between rounded border border-border bg-surface-elevated px-4 py-3 text-left transition-colors hover:border-border-strong hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div>
                  <div className="font-medium text-fg">{t.nombre}</div>
                  <div className="text-[11px] text-fg-tertiary">
                    Vas a entrar como <span className="text-fg-secondary">{prettyRole(t.rol)}</span>
                  </div>
                </div>
                {isSwitching ? (
                  <Spinner className="size-4" />
                ) : (
                  <Icon
                    name="arrow-right"
                    size={16}
                    className="text-fg-tertiary group-hover:text-fg"
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {error ? (
        <Card variant="crit-left" className="mt-4">
          <CardContent className="p-3 pt-3 text-xs text-crit">{error}</CardContent>
        </Card>
      ) : null}

      <div className="mt-6 flex justify-end">
        <Button variant="ghost" size="sm" onClick={() => signOut()}>
          Cerrar sesión
        </Button>
      </div>
    </AuthLayout>
  );
}

function prettyRole(rol: Role): string {
  const map: Record<Role, string> = {
    super_admin: 'Super Admin',
    admin_tenant: 'Admin del Tenant',
    directivo: 'Directivo',
    coordinador: 'Coordinador',
    supervisor: 'Supervisor',
    lider: 'Líder',
    pantalla: 'Pantalla de planta',
  };
  return map[rol];
}
